import * as THREE from 'three';
import { InputManager } from '@/utils/InputManager';
import { BowController } from './BowController';
import { PlayerModel } from './PlayerModel';

/**
 * Controls the player character
 */
export class PlayerController {
  // Player model
  private playerModel: PlayerModel;
  
  // Bow controller
  private bowController: BowController;
  
  // Input manager
  private inputManager: InputManager;
  
  // Scene reference
  private scene: THREE.Scene;
  
  // Camera reference
  private camera: THREE.Camera;
  
  // Movement parameters
  private moveSpeed: number = 5.0; // units per second
  private sprintMultiplier: number = 1.6;
  private jumpForce: number = 8.0;
  private gravity: number = 20.0;
  
  // Player state
  private velocity: THREE.Vector3 = new THREE.Vector3();
  private isGrounded: boolean = false;
  private isSprinting: boolean = false;
  
  // Camera parameters
  private lookSensitivity: number = 0.002;
  private maxLookAngle: number = Math.PI / 2 - 0.1; // Just under 90 degrees
  private currentLookAngle: number = 0;
  private cameraRotation: THREE.Euler = new THREE.Euler(0, 0, 0, 'YXZ'); // YXZ order is important for FPS controls
  
  /**
   * Creates a new PlayerController
   * @param scene The scene to add the player to
   * @param camera The camera to control
   * @param inputManager The input manager
   */
  constructor(scene: THREE.Scene, camera: THREE.Camera, inputManager: InputManager) {
    this.scene = scene;
    this.camera = camera;
    this.inputManager = inputManager;
    
    // Create player model
    this.playerModel = new PlayerModel();
    this.scene.add(this.playerModel.getObject());
    
    // Create bow controller
    this.bowController = new BowController(scene, camera, inputManager);
    
    // Set initial camera position
    this.camera.position.set(0, 1.7, 0); // Eye height
    
    // Setup input handlers
    this.setupInputHandlers();
    
    // Set initial position
    this.playerModel.setPosition(new THREE.Vector3(0, 2, 0));
    this.updateCameraPosition();
    
    console.log("PlayerController initialized");
  }
  
  /**
   * Sets up input handlers for player movement
   */
  private setupInputHandlers(): void {
    try {
      // Mouse movement for looking
      this.inputManager.onMouseMove((x, y, deltaX, deltaY) => {
        if (this.inputManager.isPointerLock()) {
          this.handleLook(deltaX, deltaY);
        }
      });
      
      // Jump on space key
      this.inputManager.onKeyDown((key) => {
        if (key === ' ' || key === 'space') {
          this.jump();
        } else if (key === 'shift') {
          this.isSprinting = true;
        }
      });
      
      // Stop sprinting when shift is released
      this.inputManager.onKeyUp((key) => {
        if (key === 'shift') {
          this.isSprinting = false;
        }
      });
      
      console.log("Player input handlers set up successfully");
    } catch (error) {
      console.error("Error setting up player input handlers:", error);
    }
  }
  
  /**
   * Makes the player jump if they are on the ground
   */
  private jump(): void {
    if (this.isGrounded) {
      this.velocity.y = this.jumpForce;
      this.isGrounded = false;
      console.log("Player jumped");
    }
  }
  
  /**
   * Handles player looking (camera rotation)
   * @param deltaX Mouse X movement
   * @param deltaY Mouse Y movement
   */
  private handleLook(deltaX: number, deltaY: number): void {
    // Update camera rotation Euler angles
    this.cameraRotation.y -= deltaX * this.lookSensitivity; // Horizontal rotation (yaw)
    this.cameraRotation.x -= deltaY * this.lookSensitivity; // Vertical rotation (pitch)
    
    // Clamp vertical rotation to prevent flipping
    this.cameraRotation.x = Math.max(
      -this.maxLookAngle,
      Math.min(this.maxLookAngle, this.cameraRotation.x)
    );
    
    // Update player model rotation (only horizontal rotation)
    this.playerModel.getObject().rotation.y = this.cameraRotation.y;
    
    // Apply rotation to camera
    this.camera.rotation.copy(this.cameraRotation);
    
    // Log rotation for debugging
    if (Math.random() < 0.01) { // Only log occasionally to avoid spam
      console.log(`Camera rotation: x=${this.cameraRotation.x.toFixed(2)}, y=${this.cameraRotation.y.toFixed(2)}`);
    }
  }
  
  /**
   * Updates the player controller
   * @param deltaTime Time since last update in seconds
   */
  public update(deltaTime: number): void {
    // Handle movement input
    this.handleMovementInput(deltaTime);
    
    // Apply physics
    this.applyPhysics(deltaTime);
    
    // Update camera position to follow player
    this.updateCameraPosition();
    
    // Update bow controller
    this.bowController.update(deltaTime);
  }
  
  /**
   * Handles player movement input
   * @param deltaTime Time since last update in seconds
   */
  private handleMovementInput(deltaTime: number): void {
    // Get movement direction based on WASD keys
    const moveDirection = new THREE.Vector3(0, 0, 0);
    
    if (this.inputManager.isKeyPressed('w')) {
      moveDirection.z = -1; // W = Forward (negative Z)
    }
    if (this.inputManager.isKeyPressed('s')) {
      moveDirection.z = 1; // S = Backward (positive Z)
    }
    if (this.inputManager.isKeyPressed('a')) {
      moveDirection.x = -1; // A = Left (negative X)
    }
    if (this.inputManager.isKeyPressed('d')) {
      moveDirection.x = 1; // D = Right (positive X)
    }
    
    // Normalize movement direction if moving diagonally
    if (moveDirection.length() > 0) {
      moveDirection.normalize();
    }
    
    // Apply movement speed
    let speed = this.moveSpeed;
    if (this.isSprinting) {
      speed *= this.sprintMultiplier;
    }
    
    // Convert movement direction to world space based on camera rotation
    const rotationY = this.cameraRotation.y;
    
    // Create direction vectors based on camera orientation
    const forward = new THREE.Vector3(0, 0, -1).applyAxisAngle(new THREE.Vector3(0, 1, 0), rotationY);
    const right = new THREE.Vector3(1, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), rotationY);
    
    // Calculate world movement direction
    const worldMoveDirection = new THREE.Vector3();
    worldMoveDirection.addScaledVector(forward, -moveDirection.z); // Note the negative sign here
    worldMoveDirection.addScaledVector(right, moveDirection.x);
    worldMoveDirection.y = 0; // Keep movement on the horizontal plane
    
    if (worldMoveDirection.length() > 0) {
      worldMoveDirection.normalize();
    }
    
    // Set horizontal velocity based on input
    this.velocity.x = worldMoveDirection.x * speed;
    this.velocity.z = worldMoveDirection.z * speed;
    
    // Handle jumping
    if (this.isGrounded && this.inputManager.isKeyPressed(' ')) {
      this.jump();
    }
  }
  
  /**
   * Applies physics to the player
   * @param deltaTime Time since last update in seconds
   */
  private applyPhysics(deltaTime: number): void {
    // Apply gravity
    if (!this.isGrounded) {
      this.velocity.y -= this.gravity * deltaTime;
    }
    
    // Move player
    const movement = this.velocity.clone().multiplyScalar(deltaTime);
    const newPosition = this.playerModel.getPosition().add(movement);
    this.playerModel.setPosition(newPosition);
    
    // Ground check (simplified - in a real game, use raycasting)
    if (this.playerModel.getPosition().y < 0) {
      this.playerModel.setPosition(new THREE.Vector3(
        this.playerModel.getPosition().x,
        0,
        this.playerModel.getPosition().z
      ));
      this.velocity.y = 0;
      this.isGrounded = true;
    }
  }
  
  /**
   * Updates the camera position to follow the player
   */
  private updateCameraPosition(): void {
    // Set camera position to player's eye position
    this.camera.position.copy(this.playerModel.getEyePosition());
    
    // Ensure camera rotation is applied
    this.camera.rotation.copy(this.cameraRotation);
  }
  
  /**
   * Sets the player position
   * @param position The position to set
   */
  public setPosition(position: THREE.Vector3): void {
    this.playerModel.setPosition(position);
    this.updateCameraPosition();
  }
  
  /**
   * Gets the player position
   * @returns The player position
   */
  public getPosition(): THREE.Vector3 {
    return this.playerModel.getPosition();
  }
  
  /**
   * Gets the player's current velocity
   * @returns The player's velocity vector
   */
  public getVelocity(): THREE.Vector3 {
    return this.velocity.clone();
  }
  
  /**
   * Gets the bow controller
   * @returns The bow controller
   */
  public getBowController(): BowController {
    return this.bowController;
  }
  
  /**
   * Cleans up resources when the player controller is no longer needed
   */
  public dispose(): void {
    // Remove player from scene
    this.scene.remove(this.playerModel.getObject());
    
    // Dispose of bow controller
    this.bowController.dispose();
    
    // Dispose of player model
    this.playerModel.dispose();
  }
} 